/**
 * HistorySidebar — utility functions, interfaces, and constants.
 * Keeps the .tsx file focused on the React component.
 */

import {
  SESSION_NAMES_KEY,
  AGUI_SUFFIX_REGEX,
  UNTRUSTED_INPUT_REGEX,
} from './constants';
import { SessionEvent, EventPayloadItem, SessionSummary } from './api';

// ─── Constants ───────────────────────────────────────────────────────────────

export const ROLE_USER = 'user' as const;
export const ROLE_ASSISTANT = 'assistant' as const;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  role: typeof ROLE_USER | typeof ROLE_ASSISTANT;
  content: string;
  timestamp: number;
  /**
   * AgentCore coordinate of the event holding this turn. Assistant messages only, and withheld when the
   * role discriminator collides within an event.
   *
   * Declared here rather than left to a spread: without it the property is invisible to consumers, since
   * TypeScript does not apply excess-property checks to spread expressions.
   */
  memoryRef?: { sessionId: string; eventId: string; role: 'ASSISTANT' };
}

export interface HistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSession: (sessionId: string, messages: ChatMessage[]) => void;
  currentSessionId: string | null;
  domainIdentifier?: string;
  /** When true, session buttons are disabled (e.g. during in-flight request) */
  disabled?: boolean;
}

// ─── Data Transformation ─────────────────────────────────────────────────────

/**
 * Convert raw AgentCore Memory events into ChatMessage objects.
 * Handles both `conversational` and `blob` payload formats.
 */
export function mapEventsToChatMessages(
  events: SessionEvent[],
  sessionId?: string
): ChatMessage[] {
  const messages: ChatMessage[] = [];
  /**
   * Tie broken on `eventId`, which makes this a total order.
   *
   * `Array.prototype.sort` is stable, so tied timestamps previously kept their *input* order — and
   * `ListEvents` promises no ordering, so two reloads receiving the same events in a different order
   * rendered the transcript differently. `eventId` is unique, so this is deterministic. It does not affect
   * the durable identifier, whose value never depended on position — only the rendered sequence.
   */
  const sorted = [...events].sort(
    (a, b) =>
      a.eventTimestamp - b.eventTimestamp ||
      String(a.eventId).localeCompare(String(b.eventId))
  );

  for (const event of sorted) {
    if (!event.payload) continue;

    /**
     * Role is the discriminator within an event, replacing the render position.
     *
     * One event carries both halves of an exchange, so the role separates them. A position would change
     * when anything above it changed, so the identifier would not denote the same response across reloads.
     * A repeated role within one event is disambiguated by an occurrence suffix so the id stays unique, and
     * the `memoryRef` is withheld from every occurrence after the first: role alone cannot say which of two
     * responses is the rated one. Those messages render without rating controls, which is the safe outcome.
     */
    const seenRoles = new Map<string, number>();

    for (const item of event.payload) {
      if (item.conversational) {
        messages.push(
          createMessageFromConversational(event, item, seenRoles, sessionId)
        );
      } else if (item.blob) {
        messages.push(
          ...createMessagesFromBlob(event, item, seenRoles, sessionId)
        );
      }
    }
  }

  return messages;
}

/**
 * Records an occurrence of `role` within the event and returns how many have now been seen.
 *
 * `1` means first — the id needs no suffix and the `memoryRef` may be attached. Anything higher means the
 * role has repeated, so the id must be disambiguated and the `memoryRef` withheld: two responses in one
 * event cannot be told apart by role, so neither can claim to be *the* rated answer.
 *
 * Counting rather than returning a boolean is what lets the message still render. Dropping it would keep
 * ids unique by hiding transcript content, which is a worse failure than an unrateable message.
 */
function claimRole(seenRoles: Map<string, number>, role: string): number {
  const next = (seenRoles.get(role) ?? 0) + 1;
  seenRoles.set(role, next);
  return next;
}

/** `''` for the first occurrence, `-2`, `-3`, … thereafter, so ids stay unique within an event. */
function occurrenceSuffix(occurrence: number): string {
  return occurrence === 1 ? '' : `-${occurrence}`;
}

function createMessageFromConversational(
  event: SessionEvent,
  item: EventPayloadItem,
  seenRoles: Map<string, number>,
  sessionId?: string
): ChatMessage {
  const conv = item.conversational!;
  const isUser = conv.role === 'USER';
  let text = conv.content.text;
  if (isUser) {
    // Strip AGUI format suffix and untrusted-input wrapper tags
    text = text.replace(AGUI_SUFFIX_REGEX, '').trim();
    const match = text.match(UNTRUSTED_INPUT_REGEX);
    if (match) text = match[1].trim();
  }
  const occurrence = claimRole(seenRoles, conv.role);
  const unique = occurrence === 1;
  return {
    id: `${event.eventId}-${conv.role}${occurrenceSuffix(occurrence)}`,
    role: isUser ? ROLE_USER : ROLE_ASSISTANT,
    content: text,
    timestamp: event.eventTimestamp * 1000,
    // Assistant messages only — a rating targets the answer, never the prompt. Withheld on a collision.
    ...(!isUser && unique && sessionId
      ? {
          memoryRef: {
            sessionId,
            eventId: event.eventId,
            role: 'ASSISTANT' as const,
          },
        }
      : {}),
  };
}

/**
 * Kept rather than dropped: `{"kind":"turn"}` blob events are what the Merlin Runtime writes, so they are
 * the whole of a ticket analysis session's history — and they would become a second copy of every chat turn
 * if the Runtime's detached chat write ever starts landing. `AgentCoreMemoryStore.load` decodes both
 * shapes, and so must this.
 */
function createMessagesFromBlob(
  event: SessionEvent,
  item: EventPayloadItem,
  seenRoles: Map<string, number>,
  sessionId?: string
): ChatMessage[] {
  const messages: ChatMessage[] = [];

  try {
    const blob =
      typeof item.blob === 'string' ? JSON.parse(item.blob) : item.blob;

    if (blob?.user) {
      let userText = blob.user.replace(AGUI_SUFFIX_REGEX, '').trim();
      const match = userText.match(UNTRUSTED_INPUT_REGEX);
      if (match) userText = match[1].trim();
      const userOccurrence = claimRole(seenRoles, 'USER');
      messages.push({
        // The positional index is dropped here too, not only in the conversational branch: it already
        // carried a role discriminator but *also* a render position.
        id: `${event.eventId}-USER${occurrenceSuffix(userOccurrence)}`,
        role: ROLE_USER,
        content: userText,
        timestamp: event.eventTimestamp * 1000,
      });
    }

    if (blob?.assistant) {
      const occurrence = claimRole(seenRoles, 'ASSISTANT');
      const unique = occurrence === 1;
      messages.push({
        id: `${event.eventId}-ASSISTANT${occurrenceSuffix(occurrence)}`,
        role: ROLE_ASSISTANT,
        content: blob.assistant,
        timestamp: event.eventTimestamp * 1000,
        ...(unique && sessionId
          ? {
              memoryRef: {
                sessionId,
                eventId: event.eventId,
                role: 'ASSISTANT' as const,
              },
            }
          : {}),
      });
    }
  } catch (err) {
    console.warn('[HistorySidebar] Malformed blob payload, skipping:', err);
  }

  return messages;
}

// ─── Display Helpers ─────────────────────────────────────────────────────────

export function formatSessionDate(ts: string | number): string {
  const date = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getSessionDisplayName(sessionId: string): string {
  try {
    const names = JSON.parse(localStorage.getItem(SESSION_NAMES_KEY) || '{}');
    if (names[sessionId]) return names[sessionId];
  } catch (err) {
    console.warn(
      '[HistorySidebar] Failed to read session names from localStorage:',
      err
    );
  }

  // Fallback: extract slug from session ID format "slug_uuid"
  const parts = sessionId.split('_');
  if (parts.length > 1) {
    return parts.slice(0, -1).join('_').replace(/-/g, ' ');
  }
  return sessionId;
}

export function sortSessionsByDate(
  sessions: SessionSummary[]
): SessionSummary[] {
  return [...sessions].sort((a, b) => {
    const timeA =
      typeof a.createdAt === 'number'
        ? a.createdAt
        : new Date(a.createdAt).getTime() / 1000;
    const timeB =
      typeof b.createdAt === 'number'
        ? b.createdAt
        : new Date(b.createdAt).getTime() / 1000;
    return timeB - timeA;
  });
}
